import { useCallback, useMemo, useState } from 'react'

import type { Area, Point, MediaSize } from 'react-easy-crop'

import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Slider } from '@tribeplatform/react-ui-kit/Slider'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { logger } from '../common/lib/logger.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { Cropper } from '../utils/react-easy-crop.js'

import { getCroppedImg } from './canvas.utils.js'
import { CropOptions, CropResult } from './types.js'

interface Props {
  open: boolean
  close: () => void
  initialImage: File
  onSubmit: (cropResult: CropResult) => void
  options: CropOptions
}

export const CropImageModal = ({
  initialImage,
  onSubmit,
  open,
  close,
  options,
}: Props) => {
  const {
    shape,
    aspectRatio = 1,
    restrictPosition = true,
    minZoom = 1,
    maxZoom = 3.0,
    format,
  } = options ?? {}

  const { $t } = useI18n()

  const imageSrc = useMemo(
    () => URL.createObjectURL(initialImage),
    [initialImage],
  )

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const [processing, setProcessing] = useState(false)

  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null)

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    [],
  )
  const onZoomChange = useCallback(
    newZoom => setZoom(Math.round(10 * newZoom) / 10),
    [],
  )

  const onApply = async () => {
    setProcessing(true)
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        format,
      )
      onSubmit({ croppedImage, zoom, crop })
    } catch (error) {
      logger.error(error)
      toast({
        title: $t({
          id: 'Generics.SomethingWentWrong',
          defaultMessage: 'Something went wrong',
        }),
        description: $t({
          defaultMessage:
            'We could not crop this image. Please try another one',
          id: 'CropImage.FailedToProcess.Description',
        }),
        status: 'error',
      })
      setProcessing(false)
    }
  }

  if (!initialImage || typeof window === 'undefined') {
    return null
  }

  return (
    <Modal open={open} onClose={close} transition="fade">
      <Modal.Header
        title={
          <T
            id="CropImageModal.CropImageModal.HeaderTitle"
            defaultMessage="Edit image"
          />
        }
      />
      <Modal.Content>
        <div className="space-y-3">
          <div
            className="relative"
            style={{ height: mediaSize?.height ?? 400 }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              minZoom={minZoom}
              maxZoom={maxZoom}
              onZoomChange={onZoomChange}
              cropShape={shape}
              restrictPosition={restrictPosition}
              onMediaLoaded={setMediaSize}
            />
          </div>
          <div className="flex items-center gap-3">
            <SvgIcon className="w-6 h-6" name="zoom-out" />
            <Slider
              value={zoom}
              onChange={setZoom}
              min={minZoom}
              max={maxZoom}
              step={0.01}
              aria-labelledby="Zoom"
            />
            <SvgIcon className="w-6 h-6" name="zoom-in" />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onApply} variant="primary" loading={processing}>
          <T id="Generics.Apply" defaultMessage="Apply" />
        </Button>

        <Button
          variant="secondaryNeutral"
          onClick={close}
          disabled={processing}
        >
          <T id="Generics.Cancel" defaultMessage="Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
